<script setup lang="ts">
    import { computed, onMounted, ref, watch, type Ref } from 'vue'
    import { useRoute, useRouter, onBeforeRouteLeave } from 'vue-router'
    import { EditorState } from '@common/Common'
    import CorporateNavigation from '@common/Components/CorporateNavigation.vue'
    import { guid } from '@common/Guid'
    import { CurrencyDenomination, MonthYear, MonthYearHelpers, ValidationError } from '@common/types'
    import { useOpexMetricConfigurationQueryV1 } from '@Quest/Metrics/queries-vue-composables'
    import { useCreateOpexMetricCommandV1, useUpdateOpexMetricCommandV1 } from '@Quest/Metrics/commands-vue-composables'
    import type { OpexMetricConfigurationReadModel, OpexAccountMetricItemReadModel } from '@Quest/Metrics/readmodels'
    import { ValueBalanceType, ValueCommitmentType } from '@Quest/Metrics/types'
    import MetricTimeWindowSettings from '@Quest/Metrics/Components/MetricTimeWindowSettings.vue'
    import MetricScheduleDependentSettings from '@Quest/Metrics/Components/MetricScheduleDependentSettings.vue'

    const route = useRoute()
    const router = useRouter()

    const workspaceId = computed(() => Number(route.params?.workspaceId ?? 0))
    const strategyId = computed(() => String(route.params?.strategyId ?? ''))
    const pageMode = computed(() => route.meta.mode as EditorState)
    const isEditMode = computed(() => pageMode.value === EditorState.Editor)


    const {
        runOpexMetricConfigurationQueryV1,
        modelOpexMetricConfigurationQueryV1
    } = useOpexMetricConfigurationQueryV1(workspaceId, strategyId)

    const rm = modelOpexMetricConfigurationQueryV1 as Ref<OpexMetricConfigurationReadModel | null>

    const {
        invokeCreateOpexMetricCommandV1,
        validationErrorsCreateOpexMetricCommandV1,
        onSucceedCreateOpexMetricCommandV1
    } = useCreateOpexMetricCommandV1(workspaceId, strategyId)

    const {
        invokeUpdateOpexMetricCommandV1,
        validationErrorsUpdateOpexMetricCommandV1,
        onSucceedUpdateOpexMetricCommandV1
    } = useUpdateOpexMetricCommandV1(workspaceId, strategyId)

    const initialized = ref(false)
    const errors = ref<ValidationError[]>([])
    const isSaving = ref(false)
    const allowRouteLeave = ref(false)

    const name = ref<string | null>(null)
    const description = ref<string | null>(null)

    const startDateBoundary = ref<MonthYear | null>(null)
    const endDateBoundary = ref<MonthYear | null>(null)
    const defaultProjectStartDate = ref<MonthYear | null>(null)

    const valueBalanceType = ref<ValueBalanceType>(ValueBalanceType.Costs)
    const denomination = ref<CurrencyDenomination>(CurrencyDenomination.Unit)

    const windowStartOffsetInMonths = ref<number | null>(null)
    const windowLengthInMonths = ref<number | null>(null)

    const accounts = computed(() => rm.value?.Accounts ?? [])
    const costIds = computed(() => accounts.value.filter(a => a.IsIncluded).map(a => a.Id) ?? [])

    const applyBenefitsConfidenceFactor = ref(false)
    const valueCommitmentType = ref<ValueCommitmentType>(ValueCommitmentType.Total)

    const pageHeading = computed(() => isEditMode.value ? "Edit the metric" : "New cost subtotal metric")
    const saveButtonText = computed(() => isSaving.value ? "Saving..." : "Save changes")
    const nameError = computed(() => errors.value.find(e => e.ErrorCode.startsWith("Name."))?.Error ?? null)
    const costIdsError = computed(() => errors.value.find(e => e.ErrorCode.startsWith("CostIds."))?.Error ?? null)

    const totalOpex = ref<boolean>(true)

    const hasChanges = computed(() =>
    {
        if (!isEditMode.value) return true

        const model = rm.value
        if (!model) return true

        const s = model.ScheduleDependentSettings

        return (
            name.value !== model.Name ||
            description.value !== model.Description ||
            valueBalanceType.value !== model.ValueBalanceType ||
            denomination.value !== model.Denomination ||
            windowStartOffsetInMonths.value !== model.WindowStartOffsetInMonths ||
            windowLengthInMonths.value !== model.WindowLengthInMonths ||
            applyBenefitsConfidenceFactor.value !== s?.ApplyBenefitsConfidenceFactor ||
            valueCommitmentType.value !== s?.ValueCommitmentType ||
            !MonthYearHelpers.areEqual(defaultProjectStartDate.value, s?.DefaultProjectStartDate) ||
            !MonthYearHelpers.areEqual(startDateBoundary.value, s?.StartDateBoundary) ||
            !MonthYearHelpers.areEqual(endDateBoundary.value, s?.EndDateBoundary) ||
            !areCostIdsEqualIgnoringOrder(costIds.value, model.CostIds)
        )
    })

    const groupedAccounts = computed(() =>
    {
        const groups: Record<string, OpexAccountMetricItemReadModel[]> = {}

        for (const acc of accounts.value)
        {
            if (!groups[acc.Type])
                groups[acc.Type] = []

            groups[acc.Type].push(acc)
        }

        return Object.entries(groups).map(([type, items]) => ({
            type,
            items
        }))
    })

    function handleCancel()
    {
        if (isEditMode.value)
        {
            router.go(0)
        } else
        {
            router.go(-1)
        }
    }

    function handleBack()
    {
        router.go(-1)
    }

    function handleSave()
    {
        isSaving.value = true
        errors.value = []

        const ids = totalOpex.value === true ? null : costIds.value

        const payload = {
            id: isEditMode.value ? rm.value?.Id : guid(),
            name: name.value,
            description: description.value,
            valueBalanceType: valueBalanceType.value,
            denomination: denomination.value,
            windowStartOffsetInMonths: windowStartOffsetInMonths.value,
            windowLengthInMonths: windowLengthInMonths.value,
            defaultProjectStartDate: defaultProjectStartDate.value,
            startDateBoundary: startDateBoundary.value,
            endDateBoundary: endDateBoundary.value,
            valueCommitmentType: valueCommitmentType.value,
            applyBenefitsConfidenceFactor: applyBenefitsConfidenceFactor.value,
            costIds: ids
        }

        if (isEditMode.value)
        {
            invokeUpdateOpexMetricCommandV1(payload)
        }
        else
        {
            invokeCreateOpexMetricCommandV1(payload)
        }
    }

    function areCostIdsEqualIgnoringOrder(a: string[], b: string[]): boolean
    {
        if (a === b) return true

        const setA = new Set(a)
        const setB = new Set(b)

        if (setA.size !== setB.size) return false

        for (const v of setA)
        {
            if (!setB.has(v)) return false
        }

        return true
    }


    onMounted(() =>
    {
        document.title = "Opex Metric Configuration"

        runOpexMetricConfigurationQueryV1({
            MetricId: route.params.metricId ?? null
        })
    })

    watch(rm, (model) =>
    {
        if (!model || initialized.value) return

        name.value = model.Name
        description.value = model.Description

        valueBalanceType.value = model.ValueBalanceType
        denomination.value = model.Denomination
        windowStartOffsetInMonths.value = model.WindowStartOffsetInMonths
        windowLengthInMonths.value = model.WindowLengthInMonths

        applyBenefitsConfidenceFactor.value = model.ScheduleDependentSettings?.ApplyBenefitsConfidenceFactor ?? false
        valueCommitmentType.value = model.ScheduleDependentSettings?.ValueCommitmentType ?? ValueCommitmentType.Total

        defaultProjectStartDate.value = MonthYearHelpers.copyNullable(model.ScheduleDependentSettings?.DefaultProjectStartDate)
        startDateBoundary.value = MonthYearHelpers.copyNullable(model.ScheduleDependentSettings?.StartDateBoundary)
        endDateBoundary.value = MonthYearHelpers.copyNullable(model.ScheduleDependentSettings?.EndDateBoundary)

        totalOpex.value = model.CostIds.length === 0;

        initialized.value = true
    })


    onSucceedCreateOpexMetricCommandV1(() =>
    {
        allowRouteLeave.value = true
        router.go(-1)
    })

    onSucceedUpdateOpexMetricCommandV1(() =>
    {
        allowRouteLeave.value = true
        router.go(0)
    })

    watch(totalOpex, (value) =>
    {
        if (value)
        {
            accounts.value.forEach(a => a.IsIncluded = false)
        }
    })


    watch(validationErrorsCreateOpexMetricCommandV1, (validationErrors) =>
    {
        if (!validationErrors || validationErrors.length === 0) return
        errors.value = validationErrors
        isSaving.value = false
    })

    watch(validationErrorsUpdateOpexMetricCommandV1, (validationErrors) =>
    {
        if (!validationErrors || validationErrors.length === 0) return
        errors.value = validationErrors
        isSaving.value = false
    })

    onBeforeRouteLeave((to, from, next) =>
    {
        if (allowRouteLeave.value)
        {
            next()
            return
        }

        if (hasChanges.value && !isSaving.value)
        {
            if (confirm('You have unsaved changes. Do you want to save them before leaving?'))
            {
                handleSave()
            }
            else
            {
                next()
            }
        } else
        {
            next()
        }
    })
</script>


<template>
    <div>
        <CorporateNavigation ActivePage="metrics" />

        <div v-if="initialized" class="container" style="margin-top: 80px; padding-top: 40px;">
            <div class="row">
                <div class="col-lg-6">
                    <h6>
                        <a class="text-dark" @click="handleBack" data-testid="cost-subtotal-metric-config-back">
                            <i class="fa fa-angle-double-left" aria-hidden="true"></i> Back
                        </a>
                    </h6>
                    <h1 style="font-size: 2em; font-weight: 300; color: #33475b; margin: 0;">{{pageHeading}}</h1>
                </div>

                <div class="col-lg-6 text-right align-self-end">
                    <button type="submit" form="metric-configuration-form" class="mr-1" :disabled="isSaving"
                            data-testid="cost-subtotal-metric-config-save"
                            :class="hasChanges ? 'btn btn-warning' : 'btn btn-outline-secondary'" style="padding: 0.7em 2em;">
                        {{ saveButtonText }}
                    </button>
                    <button v-if="hasChanges" type="button" class="btn btn-outline-secondary" style="padding: 0.7em 2em;" @click="handleCancel" data-testid="cost-subtotal-metric-config-cancel">
                        Cancel
                    </button>
                </div>
            </div>

            <hr class="mb-5" />

            <form id="metric-configuration-form" @submit.prevent="handleSave" class="mb-4" data-testid="cost-subtotal-metric-config-form">

                <div v-if="errors.length" class="alert alert-danger mb-4">
                    <div v-for="e in errors" :key="e.ErrorCode">
                        {{ e.Error }}
                    </div>
                </div>

                <div class="form-group mb-4">
                    <label>Metric Name:</label>
                    <input v-model.trim="name" type="text" class="form-control" required maxlength="100" minlength="2" data-testid="cost-subtotal-metric-config-name" />
                    <div class="error">{{nameError}}</div>
                </div>

                <div class="form-group mb-4">
                    <label>Metric Description:</label>
                    <textarea v-model.trim="description" class="form-control" data-testid="cost-subtotal-metric-config-description"></textarea>
                </div>

                <div class="form-row">
                    <div class="form-group mb-4 col">
                        <label for="valueBalanceType" class="form-label">Data Type:</label>
                        <select id="valueBalanceType" v-model="valueBalanceType" class="form-control" data-testid="cost-subtotal-metric-config-value-balance-type">
                            <option value="Costs">Allocations</option>
                            <option value="Benefits">Savings</option>
                            <option value="Net">Balance (Allocations - Savings)</option>
                        </select>
                    </div>
                </div>

                <div class="form-row">
                    <div class="form-group mb-4 col">
                        <label for="denomination">Currency denomination:</label>
                        <select id="denomination" v-model="denomination" class="form-control" data-testid="cost-subtotal-metric-config-denomination">
                            <option value="Unit">Unit</option>
                            <option value="Thousand">Thousand</option>
                            <option value="Million">Million</option>
                        </select>
                    </div>
                </div>

                <fieldset class="border p-3 mt-4" :class="{ 'border-danger': costIdsError }">
                    <legend class="w-auto px-2 text-primary"
                            :class="{ 'text-danger': costIdsError }">
                        Opex Accounts
                    </legend>

                    <div class="mb-3 text-muted">
                        Choose which operating expense accounts should be included in this metric.
                    </div>

                    <div v-if="costIdsError" class="text-danger mb-2">
                        {{ costIdsError }}
                    </div>

                    <!-- Total toggle -->
                    <div class="form-check mb-3">
                        <input type="checkbox"
                               class="form-check-input"
                               id="totalOpex"
                               v-model="totalOpex" />
                        <label class="form-check-label font-weight-medium" for="totalOpex">
                            Include all accounts
                        </label>
                    </div>

                    <!-- Groups -->
                    <div v-if="!totalOpex">
                        <div v-for="group in groupedAccounts" :key="group.type" class="mb-4">

                            <div class="font-weight-bold text-secondary mb-2">
                                {{ group.type }}
                            </div>

                            <div class="pl-2">
                                <div v-for="acc in group.items" :key="acc.Id" class="form-check mb-1">
                                    <input type="checkbox"
                                           class="form-check-input"
                                           :id="acc.Id"
                                           v-model="acc.IsIncluded" />
                                    <label class="form-check-label" :for="acc.Id">
                                        {{ acc.Name }}
                                    </label>
                                </div>
                            </div>
                        </div>
                    </div>
                </fieldset>


                <MetricTimeWindowSettings v-model:startOffset="windowStartOffsetInMonths"
                                          v-model:length="windowLengthInMonths" />


                <MetricScheduleDependentSettings :canDisable="false"
                                                 :isScheduleDependent="true"
                                                 v-model:defaultProjectStartDate="defaultProjectStartDate"
                                                 v-model:startDateBoundary="startDateBoundary"
                                                 v-model:endDateBoundary="endDateBoundary"
                                                 v-model:applyBenefitsConfidenceFactor="applyBenefitsConfidenceFactor"
                                                 v-model:valueCommitmentType="valueCommitmentType" />


            </form>
        </div>
    </div>
</template>
